import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { TrailheadHeader } from './TrailheadHeader';
import { useDiagMount, useDiagWatch, useLayoutDiag } from '../../utils/trailhead_diag';

interface TrailheadLayoutProps {
  personaId: string;
  sessionId?: string;
  personaName?: string;
  personaCardSlot?: React.ReactNode;
  chatTrailSlot?: React.ReactNode;
  chatMapSlot?: React.ReactNode;
  composeInputSlot?: React.ReactNode;
}

export const TrailheadLayout: React.FC<TrailheadLayoutProps> = ({
  personaId,
  sessionId,
  personaName,
  personaCardSlot,
  chatTrailSlot,
  chatMapSlot,
  composeInputSlot,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const trailRef = useRef<HTMLDivElement>(null);

  useDiagMount('layout', 'TrailheadLayout', { personaId, sessionId, personaName });
  useDiagWatch('layout', 'slots', {
    personaCard: !!personaCardSlot,
    chatTrail: !!chatTrailSlot,
    chatMap: !!chatMapSlot,
    composeInput: !!composeInputSlot,
  });
  useLayoutDiag(rootRef, 'layout', 'root');
  useLayoutDiag(trailRef, 'layout', 'chatTrail');

  return (
    <div ref={rootRef} className="flex-1 flex flex-col min-h-0 min-w-0">
      <TrailheadHeader sessionId={sessionId} />

      {/* Persona card arrives with a soft slide-from-above when the chat
          surface mounts — pairs with TrailheadPersonaTile's lift-off
          animation on click to imply a single continuous gesture. */}
      {personaCardSlot && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.32, ease: [0.32, 0.72, 0, 1], delay: 0.05 }}
          className="border-b border-gray-200 dark:border-gray-700"
        >
          {personaCardSlot}
        </motion.div>
      )}

      <div className="flex-1 flex min-h-0 min-w-0 relative">
        <div
          ref={trailRef}
          data-testid="trailhead-chat-trail"
          className="flex-1 flex flex-col min-h-0 min-w-0 relative"
        >
          {chatTrailSlot}
        </div>
        {chatMapSlot && (
          <div className="border-l border-gray-200 dark:border-gray-700 w-64 shrink-0">
            {chatMapSlot}
          </div>
        )}
      </div>

      {/* Bottom compose-input slot retained for future use; current
          chat surface owns its own input inside the chatTrailSlot. */}
      {composeInputSlot && (
        <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-2">
          {composeInputSlot}
        </div>
      )}
    </div>
  );
};
